/**
 * Application routes.
 *
 * A single typed table rather than strings scattered through components: it is
 * what makes navigation refactorable and is the seam Phase 0 §13 relies on.
 *
 * Phase 1 establishes every destination the application will have. The screens
 * behind them are placeholders until their own phase.
 */

export const ROUTES = {
  dashboard: '/',
  /** Reachable only while unauthenticated — rendered directly by `AuthGate`,
   *  never added to `AppRoutes`/`NAVIGATION` (Registration + Approval plan §11). */
  register: '/register',
  /** The verification-link landing page — reachable only while
   *  unauthenticated, rendered directly by `AuthGate` exactly like
   *  `register` above (email-verification addendum). A self-registered
   *  account never holds a session (register.ts issues none), so this is
   *  always the state a real verification click arrives in. */
  verifyEmail: '/verify-email',
  workout: '/workout',
  plans: '/plans',
  exercises: '/exercises',
  /** Detail for one exercise. Not a tab — reached from the library (Phase 4A). */
  exerciseDetail: '/exercises/:id',
  /** Create a custom exercise (Phase 4B). React Router ranks the static
   *  segment above `:id`, so this and `exerciseDetail` never collide. */
  exerciseNew: '/exercises/new',
  /** Edit a custom exercise, or override a catalogue one (Phase 4B). */
  exerciseEdit: '/exercises/:id/edit',
  history: '/history',
  /** One past session's detail — prescribed vs performed, edit-with-audit, resume/delete (Phase 7). */
  historyDetail: '/history/:id',
  progress: '/progress',
  /** Every exercise with a current record (Phase 8). */
  progressRecords: '/progress/records',
  /** One exercise's rep-max table and other current records. */
  progressRecordDetail: '/progress/records/:exerciseId',
  /** Exercise picker for trend charts (Phase 8). */
  progressTrends: '/progress/trends',
  /** One exercise's load/volume trend over time. */
  progressTrendDetail: '/progress/trends/:exerciseId',
  /** Every measurement type (Phase 8). */
  progressMeasurements: '/progress/measurements',
  /** One measurement type's logged history, trend, and entry form. */
  progressMeasurementDetail: '/progress/measurements/:typeId',
  settings: '/settings',
  /** Create a plan (Phase 5). Ranked above `planDetail` by React Router, same trick as `exerciseNew`. */
  planNew: '/plans/new',
  /** One plan's days, schedule and summary. */
  planDetail: '/plans/:id',
  /** The plan builder: days, blocks, exercises, sets, progression. */
  planEdit: '/plans/:id/edit',
  /** A specific active/paused/completed session (Phase 6). */
  workoutSession: '/workout/:sessionId',
  /** Immediate post-completion summary: volume, PRs, progression suggestions. */
  workoutSummary: '/workout/:sessionId/summary',
  /** Administration (Master Phase 11) — reachable only from Settings, gated by `AdminOnlyGate`. Never in the tab bar. */
  adminDashboard: '/settings/administration',
  adminUsers: '/settings/administration/users',
  /** One account's role/status/delete controls. */
  adminUserDetail: '/settings/administration/users/:id',
  /** The signed-in administrator's own password-change screen. */
  adminAccount: '/settings/administration/account',
  /** Admin User Usage / Traffic, Phase 1 — account and (where `liftx-sync`
   *  is bound) workout/plan/exercise activity, never workout content. */
  adminUsage: '/settings/administration/usage',
} as const;

export type RouteKey = keyof typeof ROUTES;
export type RoutePath = (typeof ROUTES)[RouteKey];

/**
 * Build a link to one exercise.
 *
 * A helper rather than template literals at each call site, so the path shape
 * lives in exactly one place — the same reason `ROUTES` exists (Phase 0 §13).
 * Ids are encoded: catalogue ids are URL-safe, but Phase 4B's user-created ones
 * are UUIDs from `crypto.randomUUID()` and there is no reason to assume forever.
 */
export function exerciseDetailPath(id: string): string {
  return `/exercises/${encodeURIComponent(id)}`;
}

export function exerciseEditPath(id: string): string {
  return `/exercises/${encodeURIComponent(id)}/edit`;
}

export function planDetailPath(id: string): string {
  return `/plans/${encodeURIComponent(id)}`;
}

export function planEditPath(id: string): string {
  return `/plans/${encodeURIComponent(id)}/edit`;
}

export function workoutSessionPath(sessionId: string): string {
  return `/workout/${encodeURIComponent(sessionId)}`;
}

export function workoutSummaryPath(sessionId: string): string {
  return `/workout/${encodeURIComponent(sessionId)}/summary`;
}

export function historyDetailPath(id: string): string {
  return `/history/${encodeURIComponent(id)}`;
}

export function progressRecordDetailPath(exerciseId: string): string {
  return `/progress/records/${encodeURIComponent(exerciseId)}`;
}

export function progressTrendDetailPath(exerciseId: string): string {
  return `/progress/trends/${encodeURIComponent(exerciseId)}`;
}

export function progressMeasurementDetailPath(typeId: string): string {
  return `/progress/measurements/${encodeURIComponent(typeId)}`;
}

export function adminUserDetailPath(id: string): string {
  return `/settings/administration/users/${encodeURIComponent(id)}`;
}

export interface NavigationItem {
  key: RouteKey;
  path: RoutePath;
  label: string;
  /** Short label for the tab bar, where space is tight. */
  tabLabel: string;
  /** Whether it appears in the bottom tab bar (Phase 0 §13: five tabs). */
  inTabBar: boolean;
  /** Phase in which the real functionality arrives. */
  arrivesInPhase: number;
}

export const NAVIGATION: readonly NavigationItem[] = [
  { key: 'dashboard', path: ROUTES.dashboard, label: 'Dashboard', tabLabel: 'Home', inTabBar: true, arrivesInPhase: 8 },
  { key: 'plans', path: ROUTES.plans, label: 'Training Plans', tabLabel: 'Plans', inTabBar: true, arrivesInPhase: 5 },
  { key: 'exercises', path: ROUTES.exercises, label: 'Exercises', tabLabel: 'Exercises', inTabBar: true, arrivesInPhase: 4 },
  { key: 'progress', path: ROUTES.progress, label: 'Progress', tabLabel: 'Progress', inTabBar: true, arrivesInPhase: 8 },
  { key: 'settings', path: ROUTES.settings, label: 'Settings', tabLabel: 'Settings', inTabBar: true, arrivesInPhase: 1 },

  // Reachable, but not tabs. Workout is a full-screen mode (Phase 0 §13) and
  // History lives under Progress; both keep their own routes.
  { key: 'workout', path: ROUTES.workout, label: 'Workout', tabLabel: 'Workout', inTabBar: false, arrivesInPhase: 6 },
  { key: 'history', path: ROUTES.history, label: 'History', tabLabel: 'History', inTabBar: false, arrivesInPhase: 7 },
] as const;

export const TAB_ITEMS = NAVIGATION.filter((item) => item.inTabBar);
